import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Calendar, MapPin, ExternalLink, Check, X, Loader2, 
  Trash2, Pencil, Link as LinkIcon, Search, RefreshCw, AlertCircle, AlertTriangle, CheckCircle,
  Upload, Sparkles, ImageIcon
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from "@/hooks/useCategories";

interface ScrapedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  is_published: boolean;
  scraped_at: string;
  ticket_status: string | null;
  last_status_check: string | null;
}

interface AdminEventsListProps {
  events: ScrapedEvent[];
  setEvents: React.Dispatch<React.SetStateAction<ScrapedEvent[]>>;
  isLoading: boolean;
  showSearch?: boolean;
  showCategory?: boolean;
}

const AdminEventsList = ({ 
  events, 
  setEvents, 
  isLoading, 
  showSearch = true,
  showCategory = false 
}: AdminEventsListProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { categories } = useCategories();
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScrapedEvent | null>(null);
  const [checkingStatusId, setCheckingStatusId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fetchUrl, setFetchUrl] = useState("");
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    title_ar: "",
    description: "",
    description_ar: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    location_ar: "",
    image_url: "",
    external_link: "",
    category: "",
  });

  const checkTicketStatus = async (eventId: string) => {
    setCheckingStatusId(eventId);
    try {
      const { data, error } = await supabase.functions.invoke('check-ticket-status', {
        body: { eventId }
      });

      if (error) throw error;

      if (data?.success && data?.results?.length > 0) {
        const result = data.results[0];
        setEvents(events.map(e => 
          e.id === eventId ? { 
            ...e, 
            ticket_status: result.status,
            last_status_check: result.checkedAt
          } : e
        ));
        toast.success(isRTL ? "تم فحص حالة التذاكر" : "Ticket status checked");
      } else {
        toast.error(isRTL ? "لم يتم العثور على معلومات" : "No status information found");
      }
    } catch (error) {
      console.error('Error checking ticket status:', error);
      toast.error(isRTL ? "حدث خطأ أثناء الفحص" : "Error checking status");
    } finally {
      setCheckingStatusId(null);
    }
  };

  const getTicketStatusBadge = (status: string | null) => {
    switch (status) {
      case 'sold_out':
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="w-3 h-3" />
            {isRTL ? "نفذت" : "Sold Out"}
          </Badge>
        );
      case 'limited':
        return (
          <Badge className="bg-orange-500 gap-1">
            <AlertTriangle className="w-3 h-3" />
            {isRTL ? "محدود" : "Limited"}
          </Badge>
        );
      case 'available':
        return (
          <Badge className="bg-green-500 gap-1">
            <CheckCircle className="w-3 h-3" />
            {isRTL ? "متاح" : "Available"}
          </Badge>
        );
      default:
        return null;
    }
  };

  const filteredEvents = events.filter((event) => {
    const title = isRTL ? (event.title_ar || event.title) : event.title;
    const location = isRTL ? (event.location_ar || event.location) : event.location;
    const query = searchQuery.toLowerCase();
    return title.toLowerCase().includes(query) || (location?.toLowerCase().includes(query) ?? false);
  });

  const toggleEventPublished = async (event: ScrapedEvent) => {
    const { error } = await supabase
      .from("scraped_events")
      .update({ is_published: !event.is_published })
      .eq("id", event.id);

    if (!error) {
      setEvents(events.map(e => e.id === event.id ? { ...e, is_published: !e.is_published } : e));
      toast.success(
        event.is_published 
          ? (isRTL ? "تم إلغاء النشر" : "Unpublished") 
          : (isRTL ? "تم النشر" : "Published")
      );
    }
  };

  const deleteEvent = async (eventId: string) => {
    const { error } = await supabase.from("scraped_events").delete().eq("id", eventId);
    if (!error) {
      setEvents(events.filter(e => e.id !== eventId));
      toast.success(isRTL ? "تم الحذف" : "Deleted");
    }
  };

  const openEditDialog = (event: ScrapedEvent) => {
    setEditingEvent(event);
    setEditForm({
      title: event.title || "",
      title_ar: event.title_ar || "",
      description: event.description || "",
      description_ar: event.description_ar || "",
      date: event.date || "",
      start_time: event.start_time || "",
      end_time: event.end_time || "",
      location: event.location || "",
      location_ar: event.location_ar || "",
      image_url: event.image_url || "",
      external_link: event.external_link || "",
      category: event.category || "",
    });
    setUploadedImage(null);
    setImagePreview(null);
    setFetchUrl("");
    setIsEditDialogOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(isRTL ? "يرجى اختيار ملف صورة" : "Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB");
      return;
    }

    setUploadedImage(file);
    setEditForm({ ...editForm, image_url: "" });
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `events/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("event-images")
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from("event-images")
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleFetchEventData = async () => {
    if (!fetchUrl.trim()) {
      toast.error(isRTL ? "يرجى إدخال رابط الفعالية" : "Please enter event URL");
      return;
    }

    setIsFetching(true);

    try {
      const { data, error } = await supabase.functions.invoke("fetch-event-data", {
        body: { url: fetchUrl },
      });

      if (error) throw error;

      if (data?.success && data?.data) {
        const eventData = data.data;
        
        setEditForm((prev) => ({
          ...prev,
          title: eventData.title || prev.title,
          title_ar: eventData.title_ar || prev.title_ar,
          description: eventData.description || prev.description,
          description_ar: eventData.description_ar || prev.description_ar,
          date: eventData.date || prev.date,
          location: eventData.location || prev.location,
          location_ar: eventData.location_ar || prev.location_ar,
          image_url: eventData.image_url || prev.image_url,
          external_link: eventData.external_link || prev.external_link,
        }));

        toast.success(isRTL ? "تم جلب البيانات بنجاح" : "Data fetched successfully");
      } else {
        throw new Error(data?.error || "Failed to fetch");
      }
    } catch (error: any) {
      console.error("Error fetching event data:", error);
      toast.error(isRTL ? "فشل في جلب البيانات" : "Failed to fetch event data");
    } finally {
      setIsFetching(false);
    }
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    
    setIsUpdating(true);

    try {
      let imageUrl = editForm.image_url;

      if (uploadedImage) {
        setIsUploading(true);
        try {
          imageUrl = await uploadImage(uploadedImage) || "";
        } catch (error) {
          console.error("Image upload failed:", error);
          toast.error(isRTL ? "فشل في رفع الصورة" : "Failed to upload image");
          setIsUpdating(false);
          setIsUploading(false);
          return;
        }
        setIsUploading(false);
      }

      const { error } = await supabase
        .from("scraped_events")
        .update({
          title: editForm.title,
          title_ar: editForm.title_ar || null,
          description: editForm.description || null,
          description_ar: editForm.description_ar || null,
          date: editForm.date || null,
          start_time: editForm.start_time || null,
          end_time: editForm.end_time || null,
          location: editForm.location || null,
          location_ar: editForm.location_ar || null,
          image_url: imageUrl || null,
          external_link: editForm.external_link || null,
          category: editForm.category || null,
        })
        .eq("id", editingEvent.id);

      if (!error) {
        setEvents(events.map(e => 
          e.id === editingEvent.id ? { 
            ...e, 
            title: editForm.title,
            title_ar: editForm.title_ar || null,
            description: editForm.description || null,
            description_ar: editForm.description_ar || null,
            date: editForm.date || null,
            start_time: editForm.start_time || null,
            end_time: editForm.end_time || null,
            location: editForm.location || null,
            location_ar: editForm.location_ar || null,
            image_url: imageUrl || null,
            external_link: editForm.external_link || null,
            category: editForm.category || null,
          } : e
        ));
        toast.success(isRTL ? "تم تحديث الفعالية" : "Event updated");
        setIsEditDialogOpen(false);
        setEditingEvent(null);
      } else {
        toast.error(isRTL ? "حدث خطأ أثناء التحديث" : "Error updating event");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      {showSearch && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="search"
              placeholder={isRTL ? "البحث..." : "Search..."}
              className="pl-10 pr-4"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {filteredEvents.length === 0 ? (
        <Card className="p-6">
          <div className="text-center text-gray-500">
            {isRTL ? "لا توجد فعاليات حالياً" : "No events available"}
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => (
            <div 
              key={event.id} 
              className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-muted/50 transition-colors bg-white overflow-hidden"
            >
              <div className="flex items-start gap-3 w-full sm:w-auto">
                {event.image_url && (
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg shrink-0"
                  />
                )}
                <div className="flex items-center gap-1.5 sm:hidden flex-wrap">
                  {getTicketStatusBadge(event.ticket_status)}
                  <Badge variant={event.is_published ? "default" : "secondary"} className="text-xs">
                    {event.is_published 
                      ? (isRTL ? "منشور" : "Published") 
                      : (isRTL ? "مسودة" : "Draft")}
                  </Badge>
                </div>
              </div>
              <div className="flex-1 min-w-0 overflow-hidden w-full">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold truncate max-w-[250px] sm:max-w-[400px] text-sm sm:text-base" title={isRTL ? (event.title_ar || event.title) : event.title}>
                    {(() => {
                      const title = isRTL ? (event.title_ar || event.title) : event.title;
                      try {
                        const decoded = decodeURIComponent(title);
                        return decoded.length > 60 ? decoded.slice(0, 60) + '...' : decoded;
                      } catch {
                        return title.length > 60 ? title.slice(0, 60) + '...' : title;
                      }
                    })()}
                  </h3>
                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    {getTicketStatusBadge(event.ticket_status)}
                    <Badge variant={event.is_published ? "default" : "secondary"}>
                      {event.is_published 
                        ? (isRTL ? "منشور" : "Published") 
                        : (isRTL ? "مسودة" : "Draft")}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1 mt-1 break-all">
                  {event.description || (isRTL ? "لا يوجد وصف" : "No description")}
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {event.date || "-"}
                  </span>
                  <span className="flex items-center gap-1 truncate max-w-[200px]">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {isRTL ? (event.location_ar || event.location || "-") : (event.location || "-")}
                  </span>
                  {showCategory && event.category && (
                    <Badge variant="outline" className="text-xs">
                      {event.category}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {event.source_name}
                  </Badge>
                </div>
                {event.external_link && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-primary overflow-hidden">
                    <LinkIcon className="w-3 h-3 shrink-0" />
                    <a 
                      href={event.external_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline truncate max-w-[300px]"
                      title={event.external_link}
                    >
                      {(() => {
                        try {
                          const url = new URL(event.external_link);
                          const path = decodeURIComponent(url.pathname).slice(0, 30);
                          return `${url.hostname}${path}${url.pathname.length > 30 ? '...' : ''}`;
                        } catch {
                          return event.external_link.slice(0, 50) + (event.external_link.length > 50 ? '...' : '');
                        }
                      })()}
                    </a>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
                {event.external_link && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => checkTicketStatus(event.id)}
                    disabled={checkingStatusId === event.id}
                    title={isRTL ? "فحص حالة التذاكر" : "Check ticket status"}
                  >
                    {checkingStatusId === event.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(event)}
                  title={isRTL ? "تعديل" : "Edit"}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant={event.is_published ? "outline" : "default"}
                  onClick={() => toggleEventPublished(event)}
                  title={event.is_published ? (isRTL ? "إلغاء النشر" : "Unpublish") : (isRTL ? "نشر" : "Publish")}
                >
                  {event.is_published ? (
                    <X className="w-4 h-4" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteEvent(event.id)}
                  title={isRTL ? "حذف" : "Delete"}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{isRTL ? "تعديل الفعالية" : "Edit Event"}</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <div className="space-y-6 py-4">
              {/* جلب البيانات تلقائياً */}
              <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="w-4 h-4 text-primary" />
                  {isRTL ? "تحديث البيانات من رابط (اختياري)" : "Update from URL (Optional)"}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={fetchUrl}
                    onChange={(e) => setFetchUrl(e.target.value)}
                    placeholder="https://example.com/event-page"
                    dir="ltr"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleFetchEventData}
                    disabled={isFetching || !fetchUrl.trim()}
                    className="gap-2 shrink-0"
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isRTL ? "جاري الجلب..." : "Fetching..."}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        {isRTL ? "جلب" : "Fetch"}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* العناوين */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? "العنوان (عربي)" : "Title (Arabic)"}</Label>
                  <Input
                    value={editForm.title_ar}
                    onChange={(e) => setEditForm({ ...editForm, title_ar: e.target.value })}
                    placeholder={isRTL ? "عنوان الفعالية بالعربي" : "Event title in Arabic"}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? "العنوان (إنجليزي)" : "Title (English)"}</Label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder={isRTL ? "عنوان الفعالية بالإنجليزي" : "Event title in English"}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* الوصف */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? "الوصف (عربي)" : "Description (Arabic)"}</Label>
                  <Textarea
                    value={editForm.description_ar}
                    onChange={(e) => setEditForm({ ...editForm, description_ar: e.target.value })}
                    placeholder={isRTL ? "وصف الفعالية بالعربي" : "Event description in Arabic"}
                    dir="rtl"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? "الوصف (إنجليزي)" : "Description (English)"}</Label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder={isRTL ? "وصف الفعالية بالإنجليزي" : "Event description in English"}
                    dir="ltr"
                    rows={3}
                  />
                </div>
              </div>

              {/* القسم */}
              <div className="space-y-2">
                <Label>{isRTL ? "القسم" : "Category"}</Label>
                <Select
                  value={editForm.category}
                  onValueChange={(value) => setEditForm({ ...editForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isRTL ? "اختر القسم" : "Select category"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="events">{isRTL ? "الفعاليات" : "Events"}</SelectItem>
                    <SelectItem value="exhibitions">{isRTL ? "المعارض" : "Exhibitions"}</SelectItem>
                    <SelectItem value="festivals">{isRTL ? "المهرجانات" : "Festivals"}</SelectItem>
                    <SelectItem value="seasons">{isRTL ? "المواسم" : "Seasons"}</SelectItem>
                    <SelectItem value="conferences">{isRTL ? "المؤتمرات والندوات" : "Conferences"}</SelectItem>
                    <SelectItem value="occasions">{isRTL ? "المناسبات العامة" : "Public Occasions"}</SelectItem>
                    {categories
                      .filter((cat) => cat.category_slug && !["events", "exhibitions", "festivals", "seasons", "conferences", "occasions"].includes(cat.category_slug))
                      .map((cat) => (
                        <SelectItem key={cat.id} value={cat.category_slug || cat.path.replace("/", "")}>
                          {isRTL ? cat.name_ar : cat.name_en}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* التاريخ والوقت */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {isRTL ? "التاريخ" : "Date"}
                  </Label>
                  <Input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? "وقت البداية" : "Start Time"}</Label>
                  <Input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? "وقت النهاية" : "End Time"}</Label>
                  <Input
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                  />
                </div>
              </div>

              {/* الموقع */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {isRTL ? "الموقع (عربي)" : "Location (Arabic)"}
                  </Label>
                  <Input
                    value={editForm.location_ar}
                    onChange={(e) => setEditForm({ ...editForm, location_ar: e.target.value })}
                    placeholder={isRTL ? "موقع الفعالية بالعربي" : "Event location in Arabic"}
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {isRTL ? "الموقع (إنجليزي)" : "Location (English)"}
                  </Label>
                  <Input
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder={isRTL ? "موقع الفعالية بالإنجليزي" : "Event location in English"}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* رفع الصورة أو رابط الصورة */}
              <div className="space-y-4">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  {isRTL ? "صورة الفعالية" : "Event Image"}
                </Label>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {!uploadedImage && !editForm.image_url && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {isRTL ? "اضغط لرفع صورة (الحد الأقصى 5MB)" : "Click to upload image (max 5MB)"}
                    </p>
                  </div>
                )}

                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-w-xs h-40 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                      onClick={removeUploadedImage}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">{uploadedImage?.name}</p>
                  </div>
                )}

                {!uploadedImage && (
                  <div className="space-y-2">
                    {editForm.image_url && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-xs text-muted-foreground">{isRTL ? "أو" : "or"}</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    )}
                    <Input
                      value={editForm.image_url}
                      onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                      placeholder={isRTL ? "أدخل رابط الصورة" : "Enter image URL"}
                      dir="ltr"
                    />
                    {editForm.image_url && (
                      <div className="relative inline-block">
                        <img
                          src={editForm.image_url}
                          alt="Preview"
                          className="w-full max-w-xs h-40 object-cover rounded-lg border"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                          onClick={() => setEditForm({ ...editForm, image_url: "" })}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* الرابط الخارجي */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  {isRTL ? "رابط التذاكر / الموقع الخارجي" : "Tickets / External Link"}
                </Label>
                <Input
                  value={editForm.external_link}
                  onChange={(e) => setEditForm({ ...editForm, external_link: e.target.value })}
                  placeholder="https://example.com/tickets"
                  dir="ltr"
                />
                {editForm.external_link && (
                  <a 
                    href={editForm.external_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {isRTL ? "معاينة الرابط" : "Preview link"}
                  </a>
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
                <Button onClick={updateEvent} disabled={isUpdating} className="gap-2">
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isUploading 
                        ? (isRTL ? "جاري رفع الصورة..." : "Uploading...")
                        : (isRTL ? "جاري الحفظ..." : "Saving...")}
                    </>
                  ) : (
                    isRTL ? "حفظ التغييرات" : "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminEventsList;
